<template>
  <v-text-field
    v-bind:class="'number-field-' + options.type"
    v-model="cmpValue"
    v-bind:hint="hint || ((options.type === TYPES.TYPE_PHONE && !hint) ? 'Include country code (+) outside the US' : hint)"
    v-bind:persistent-hint="persistentHint"
    v-bind:label="label"
    v-bind:placeholder="placeholder"
    v-bind:readonly="readonly"
    v-bind:autofocus="autofocus"
    v-bind:disabled="disabled"
    v-bind:outlined="outlined"
    v-bind:dense="dense"
    v-bind:hide-details="hideDetails"
    v-bind:error="error"
    v-bind:error-messages="errorMessages"
    v-bind:rules="rules.concat(builtInRules())"
    v-bind:clearable="clearable"
    v-bind:backgroundColor="backgroundColor"
    v-bind:prefix="getOptions().prefix"
    v-bind:suffix="getOptions().suffix"
    v-bind="properties"
    v-on:keypress="keyPress"
    v-on:blur="onBlur"
    v-bind:append-outer-icon="getOptions().appendOuterIcon"
    v-bind:append-icon="getOptions().appendIcon"
    v-bind:prepend-inner-icon="getOptions().prependInnerIcon"
    @focus="$event.target.select()"
  >
    <!-- pass through scoped slots -->
    <template
      v-for="(_, scopedSlotName) in $scopedSlots"
      v-slot:[scopedSlotName]="slotData"
    >
      <slot :name="scopedSlotName" v-bind="slotData" />
    </template>
    <!-- pass through normal slots -->
    <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
      <slot :name="slotName" />
    </template>
  </v-text-field>
</template>

<script>
import {parsePhoneNumber, AsYouType, isValidPhoneNumber} from 'libphonenumber-js'
import orderMixin from '@/mixins/order'

export default {
  model: { prop: 'value', event: 'input' },
  props: {
    value: {
      type: [String, Number],
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
    persistentHint: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [Boolean, String],
      default: false,
    },
    errorMessages: {
      type: [Array, String],
      default: () => [],
    },
    rules: {
      type: [Array, String],
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: 'white',
    },
    valueWhenIsEmpty: {
      type: Object,
      default: null,
    },
    valueOptions: {
      type: Object,
      default: function() {
        return {
          min: 0,
          minEvent: 'SetValueMin',
        }
      },
    },
    options: {
      type: Object,
      default: function() {
        return {
          locale: 'en-US',
          prefix: '',
          suffix: '',
          length: 11,
          minPrecision: 0,
          maxPrecision: 4,
          appendOuterIcon: ``,
          appendIcon: ``,
          prependInnerIcon: ``
        }
      },
    },
    // Other v-text-field properties
    properties: {
      type: Object,
      default: function() {
        return {}
      },
    },
    type: {
      type: String,
    },
  },
  data: () => ({
    phoneData: {},
    asYouType: null,
    TYPES: {
      TYPE_DOLLAR: `dollar`,
      TYPE_PERCENT: `percent`,
      TYPE_PHONE: `phone`,
      TYPE_QTY: `qty`,
      TYPE_FRACTIONAL_QTY: `fractionalQty`
    },
    underlyingValue: null,
  }),
  computed: {
    cmpValue: {
      get: function() {
        return this.value !== null && this.value !== ''
          ? this.humanFormat(this.value.toString())
          : this.valueWhenIsEmpty
      },
      set: function(newValue) {
        this.underlyingValue = newValue
        this.$emit('input', this.machineFormat(newValue))
      },
    },
  },
  mixins: [orderMixin],
  methods: {
    builtInRules: function() {
      return (this.options.type === this.TYPES.TYPE_PHONE)
        ? [(v) => {
          return (
            v === undefined ||
            v === null ||
            !this.phoneData ||
            this.phoneData.isValidPhoneNumber ||
            `Please provide a valid phone number`
          )
        }]
        : []
    },
    getOptions: function() {
      switch (this.options.type) {
        case this.TYPES.TYPE_DOLLAR:
          return Object.assign(this.options, {
            prefix: `$`,
            minPrecision: 2,
            maxPrecision: 2,
          })
        case this.TYPES.TYPE_PERCENT:
          return Object.assign(this.options, {
            suffix: `%`,
            minPrecision: 0,
            maxPrecision: 4,
          })
        case this.TYPES.TYPE_QTY:
          return Object.assign(this.options, {
            minPrecision: 0,
            maxPrecision: 0,
          })
        case this.TYPES.TYPE_PHONE:
          return Object.assign(this.options, {
            minPrecision: 0,
            maxPrecision: 0,
            appendIcon: `mdi-phone`,
          })
        case this.TYPES.TYPE_FRACTIONAL_QTY:
          return Object.assign(this.options, {
            minPrecision: 0,
            maxPrecision: 3,
          })
      }
      return this.options
    },
    formatPhone: function(number) {
      let formattedPhone = number;
      const previousPhoneData = {...this.phoneData};
      try {
        this.phoneData = parsePhoneNumber(number, `US`);
        this.phoneData.isValidPhoneNumber = isValidPhoneNumber(number, this.phoneData.country)
        this.asYouType = new AsYouType(`US`).input(number)
        formattedPhone = this.asYouType
      } catch (e) {
        this.phoneData = {
          e,
          isValidPhoneNumber: false
        }
      }
      if (JSON.stringify(this.phoneData) !== JSON.stringify(previousPhoneData)) this.$emit('phoneData', this.phoneData)
      return formattedPhone
    },
    humanFormat: function(number) {
      if (this.options.type === this.TYPES.TYPE_PHONE) {
        const formatted = this.formatPhone(number)
        return formatted
      } else {
        const isNegative = this.isNegative(number)
        if (isNaN(number)) {
          number = ''
        } else {
          const options = {
            maximumFractionDigits: this.getOptions().maxPrecision,
            minimumFractionDigits: this.getOptions().minPrecision,
          }
          number = Number(number).toLocaleString(
            this.getOptions().locale,
            options
          )
        }
        return isNegative && number.indexOf(`-`) === -1 ? `-${number}` : number
      }
    },
    calculatePrecision(number) {
      let calc = 0
      if (typeof number === `string` && number.indexOf(`.`) > -1) {
        const parts = number.split(`.`)
        calc = parts[1].length
      }
      if (calc < this.getOptions().minPrecision)
        return this.getOptions().minPrecision
      if (calc > this.getOptions().maxPrecision)
        return this.getOptions().maxPrecision
      return calc
    },
    isNegative(number) {
      if (typeof number === `string` && number.indexOf(`-`) > -1) return true
      return number < 0
    },
    machineFormat(number) {
      if (this.options.type === this.TYPES.TYPE_PHONE) {
        return this.formatPhone(number)
      } else {
        const precision = this.calculatePrecision(number)
        const isNegative = this.isNegative(number)
        if (number) {
          // For currency (TYPE_DOLLAR), always use cents-input behavior
          // Extract all digits from input (ignoring decimal/formatting) and treat as cents
          // This allows users to type "123" and get "1.23", or continue typing after "0.01" to get "0.12"
          if (this.options.type === this.TYPES.TYPE_DOLLAR && precision > 0) {
            // Extract all digits from input, ignoring decimal point and formatting
            // cleanNumber already extracts only digits, so it handles decimals automatically
            const allDigits = this.cleanNumber(String(number))
            // Treat all digits as cents, convert to dollars
            number = allDigits.padStart(parseInt(precision) + 1, '0')
            number =
              number.substring(0, number.length - parseInt(precision)) +
              '.' +
              number.substring(number.length - parseInt(precision), number.length)
          } else {
            // Preserve decimal point when calculating precision to avoid losing it during cleanNumber
            const originalString = String(number)
            const hasDecimal = originalString.indexOf('.') > -1
            const decimalParts = hasDecimal ? originalString.split('.') : [originalString, '']
            
            // Clean the integer part (before decimal)
            const integerPart = this.cleanNumber(decimalParts[0] || '0')
            // Preserve the decimal part (after decimal point)
            const decimalPart = decimalParts[1] || ''
            
            // If we have a decimal part or precision > 0, format with decimal
            if (precision > 0 || decimalPart.length > 0) {
              // Pad decimal part to match precision
              const paddedDecimal = decimalPart.padEnd(precision, '0').substring(0, precision)
              number = integerPart + '.' + paddedDecimal
            } else {
              // No decimal needed, just use cleaned integer part
              number = integerPart || '0'
            }
          }
          
          if (isNaN(parseFloat(number))) {
            number = this.valueWhenIsEmpty
          }
        } else {
          number = this.valueWhenIsEmpty
        }
        if (precision === 0) {
          number = this.cleanNumber(number)
        }
        return isNegative ? `-${number}` : number
      }
    },
    keyPress($event) {
      let keyCode = $event.keyCode ? $event.keyCode : $event.which
      // @todo could make this configurable in options
      const allowPlus =
        (this.underlyingValue === null ||
        this.underlyingValue.toString().indexOf(`+`) === -1) &&
        this.options.type === this.TYPES.TYPE_PHONE
      const allowNegative =
        (this.underlyingValue === null ||
        this.underlyingValue.toString().indexOf(`-`) === -1) &&
        this.options.type !== this.TYPES.TYPE_PHONE
      const allowDot =
        this.getOptions().minPrecision === 0 &&
        this.getOptions().maxPrecision > 0 &&
        this.underlyingValue !== null &&
        this.underlyingValue.toString().indexOf(`.`) === -1 &&
        this.options.type !== this.TYPES.TYPE_PHONE
      if (
        (keyCode < 48 || keyCode > 57) &&
        (keyCode !== 46 || allowDot === false) &&
        (keyCode !== 45 || allowNegative === false) &&
        (keyCode !== 43 || allowPlus === false)
      ) {
        $event.preventDefault()
      }
      if (this.targetLength()) $event.preventDefault()
    },
    cleanNumber: function(value) {
      let result = ''
      if (value) {
        let flag = false
        let arrayValue = value.toString().split('')
        for (let i = 0; i < arrayValue.length; i++) {
          if (this.isInteger(arrayValue[i])) {
            if (!flag) {
              if (arrayValue[i] !== '0') {
                result = result + arrayValue[i]
                flag = true
              }
            } else {
              result = result + arrayValue[i]
            }
          }
        }
      }
      return result
    },
    isInteger(value) {
      let result = false
      if (Number.isInteger(parseInt(value))) {
        result = true
      }
      return result
    },
    targetLength() {
      if (this.value === null) return false
      return (
        Number(this.cleanNumber(this.value).length) >=
        Number(this.getOptions().length)
      )
    },
    onBlur() {
      if (
        (this.value === null || (this.value && this.value.length === 0)) ||
        parseFloat(this.value) <= this.valueOptions.min
      )
        this.$emit(
          this.valueOptions.minEvent || 'SetValueMin',
          this.valueOptions.min
        )
      this.$emit('blur') //propagate blur event
    },
  },
}
</script>

<style lang="sass">
.number-field-dollar input,
.number-field-percent input,
.number-field-qty input,
.number-field-fractionalQty input
  text-align: right !important
.number-field-phone input
  text-align: left !important
</style>
